import { useEffect } from 'react';
import type { MouseEvent } from 'react';

import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';

import { CONTACT_SUPPORT_URL } from '../../../config/constants';
import { analytics } from '../../../services/analytics';
import { intercom } from '../../../services/intercom';
import { EnumOpenDialog } from '../../../stores/ui/types';
import { useStores } from '../../../stores/useStores';
import { Dialog } from '../../Dialog/Dialog';
import { InfoIcon } from '../../SVGIcons/InfoIcon';
import Tooltip from '../../Tooltip';

import classes from './FundsHistoryDialog.module.scss';
import { FundsHistoryDialogTransactionRow } from './FundsHistoryDialogTransactionRow';

const FundsHistoryDialogNew = observer(() => {
  const { appStore, uiStore } = useStores();
  const { t } = useTranslation();

  const isOpen = uiStore.openDialog.dialog === EnumOpenDialog.isOpenFundsHistoryDialogNew;
  const callback = uiStore.openDialog.options?.onBackClick;

  useEffectOnce(() => {
    void analytics.sendEvent('funds_history', {
      event_category: 'funds_history',
      event_action: 'impression',
    });
  });
  useEffect(() => {
    if (isOpen) {
      void analytics.sendEvent('info_icon', {
        event_category: 'funds_history',
        event_action: 'click',
      });
      void appStore.getTransactions();
    }
  }, [appStore, isOpen]);

  const bingoPurchaseSources = ['bingo_store', 'subscription'];

  const onBack =
    typeof callback === 'function'
      ? () => {
          callback();
          uiStore.setCloseDialog();
        }
      : undefined;

  const onClose = () => {
    void analytics.sendEvent('funds_history', {
      event_category: 'funds_history',
      event_action: 'click',
      click_location: 'close',
    });
    if (onBack) {
      onBack();
    } else {
      uiStore.setCloseDialog();
    }
  };

  const onBackdropClick = () => {
    if (onBack) {
      onBack();
    } else {
      uiStore.setCloseDialog();
    }
  };
  const onContactSupportClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    void analytics.sendEvent('contact_support', {
      event_category: 'funds_history',
      event_action: 'click',
      cta_button: 'contact_support',
    });
    intercom.handleOpenSupport();
  };

  const onTooltipClick = (status: string) => {
    void analytics.sendEvent('funds_status', {
      event_category: 'funds_history',
      event_type: 'status',
      event_value: status,
      event_action: 'click',
    });
  };

  return (
    <div className={classes.fundsHistoryDialog}>
      <Dialog
        classNameBody={classes.dialogBody}
        classNameParent={classes.dialog}
        headerLeftElement={<img alt="Funds History" className={classes.titleImage} src="myAccount/funds_history.png" />}
        isOpen={isOpen}
        loading={false}
        newDialogStyleEnabled={true}
        title={
          <div className={classes.titleWrapper}>
            <div className={classes.titleTextGroup}>
              <span>{t('profile.fundsHistoryLowerCase')}</span>
              <Tooltip
                supportsNewTooltipDesign
                content={
                  <div
                    className={classes.tooltipContent}
                    data-testid="funds-history-dialog-tooltip-text"
                    style={{
                      minWidth: '200px',
                      maxWidth: '250px',
                      padding: '9px',
                      fontWeight: '300',
                    }}
                  >
                    <span>{t('profile.fundsHistoryTooltip')}</span>
                    <div>
                      <a
                        href={CONTACT_SUPPORT_URL}
                        rel="noreferrer"
                        style={{ color: 'rgba(1, 219, 128, 1)', textDecoration: 'underline' }}
                        target="_blank"
                        onClick={onContactSupportClick}
                      >
                        {t('common.contactSupport')}
                      </a>
                    </div>
                  </div>
                }
                dataTestId="funds-history-tooltip"
                offset={[0, 5]}
                wrapperClassName={classes.tooltipWrapper}
              >
                <InfoIcon className={classes.tooltipIcon} color="purple" />
              </Tooltip>
            </div>
          </div>
        }
        onBackdropClick={onBackdropClick}
        onClose={onClose}
      >
        <div className={classes.inner}>
          <div className={classes.fundsHistory} data-testid="funds-history-list">
            {appStore.transactions.map((item) => (
              <FundsHistoryDialogTransactionRow
                key={item.id}
                bingoPurchaseSources={bingoPurchaseSources}
                item={item}
                onTooltipClick={onTooltipClick}
              />
            ))}
          </div>

          <div className={classes.footer}>
            <div className={classes.footerText} data-testid="funds-history-need-help">
              <Trans
                components={{
                  support: (
                    <a
                      className={classes.link}
                      data-testid="funds-history-dialog-contact-support"
                      href={CONTACT_SUPPORT_URL}
                      rel="noreferrer"
                      target="_blank"
                      onClick={onContactSupportClick}
                    />
                  ),
                }}
                i18nKey="profile.needHelpContactSupport"
              />
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
});

export default FundsHistoryDialogNew;
